import { supabase } from '../lib/supabase';

export interface StatusChangeNotification {
  type: 'status_change';
  documentId: string;
  statusType: 'uploaded' | 'approved' | 'rejected' | 'revision_requested';
  userId?: string;
  organizationId?: string;
  organizationName?: string;
  additionalEmails?: string[];
}

export interface DueDateReminderNotification {
  type: 'due_date_reminder';
  requirementId: string;
  daysBeforeDue: number;
  userId?: string;
  additionalEmails?: string[];
}

export interface UserInvitationNotification {
  type: 'user_invitation';
  userData: {
    email: string;
    fullName: string;
    password: string;
    organizationName: string;
    confirmationUrl?: string;
  };
  additionalEmails?: string[];
}

export type NotificationRequest = StatusChangeNotification | DueDateReminderNotification | UserInvitationNotification;

export const notificationService = {
  /**
   * Send a notification using the Supabase Edge Function
   */
  async sendNotification(request: NotificationRequest): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('notification-service', {
        body: JSON.stringify(request)
      });

      if (error) {
        console.error('Notification service error:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error sending notification:', error);
      return { success: false, error: 'Failed to send notification' };
    }
  },

  /**
   * Send document status change notification
   */
  async sendStatusChangeNotification(
    documentId: string, 
    statusType: 'uploaded' | 'approved' | 'rejected' | 'revision_requested',
    organizationId?: string,
    organizationName?: string,
    additionalEmails?: string[]
  ): Promise<boolean> {
    const result = await this.sendNotification({
      type: 'status_change',
      documentId,
      statusType,
      organizationId,
      organizationName,
      additionalEmails
    });
    return result.success;
  },

  /**
   * Send due date reminder notification
   */
  async sendDueDateReminder(requirementId: string, daysBeforeDue: number): Promise<boolean> {
    const result = await this.sendNotification({
      type: 'due_date_reminder',
      requirementId,
      daysBeforeDue
    });
    return result.success;
  },

  /**
   * Send user invitation notification
   */
  async sendUserInvitation(userData: {
    email: string;
    fullName: string;
    password: string;
    organizationName: string;
    confirmationUrl?: string;
  }): Promise<boolean> {
    const result = await this.sendNotification({
      type: 'user_invitation',
      userData
    });
    return result.success;
  },

  /**
   * Get notification settings for an organization
   */
  async getNotificationSettings(organizationId?: string) {
    const query = supabase
      .from('notification_settings')
      .select('*');
    
    if (organizationId) {
      query.eq('organization_id', organizationId);
    } else {
      query.is('organization_id', null);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching notification settings:', error);
      return [];
    }
    
    return data || [];
  },

  /**
   * Update notification settings
   */
  async updateNotificationSettings(
    id: string,
    settings: {
      is_enabled?: boolean | null;
      days_before_due?: number[] | null;
    }
  ) {
    const { data, error } = await supabase
      .from('notification_settings')
      .update(settings)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating notification settings:', error);
      throw error;
    }

    return data;
  },

  /**
   * Get notification logs for a user
   */
  async getNotificationLogs(userId?: string, limit = 50) {
    const query = supabase
      .from('notification_logs')
      .select(`
        *,
        users(full_name, email)
      `)
      .order('email_sent_at', { ascending: false })
      .limit(limit);
    
    if (userId) {
      query.eq('user_id', userId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching notification logs:', error);
      return [];
    }
    
    return data || [];
  }
};